import { Button, Divider, FormControlLabel, Paper, Switch, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { FileDownload } from "@mui/icons-material";

import { useTheme } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../reducers/userSlice";
import { selectRTFO, setIsLoadingData, toggleStageFracModelLock } from "../../reducers/rtfoSlice";
import { RTFOFracModelChart } from "./RTFOFracModelChart";
import { RTFOFracModelDataTable } from "./RTFOFracModelDataTable";
import { RTFOFracModelDerivativeChart } from "./RTFOFracModelDerivativeChart";
import { useEffect, useState } from "react";
import { Cancel, Save, VerticalAlignBottom } from "@mui/icons-material";
import { useSocket } from "../../context/useSocket";
import { useSnackbar } from "notistack";
import { RTFOFracModelHalfLengthProbabilityChart } from "./RTFOFracModelHalfLengthProbabilityChart";
import { RTFOFracModelFracAreaProbabilityChart } from "./RTFOFracModelFracAreaProbabilityChart";

export const RTFOFracModel = ({ currentWellName }) => {

  const theme = useTheme();
  const dispatch = useDispatch();
  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];

  const [editMode, setEditMode] = useState(false);
  const [linearFlowStart, setLinearFlowStart] = useState(currentStage?.fracModel?.startLinearFlowDT);
  const [linearFlowEnd, setLinearFlowEnd] = useState(currentStage?.fracModel?.endLinearFlowDT);

  useEffect(() => {
    if (currentStage?.fracModel?.startLinearFlowDT > 0) {
      setLinearFlowStart(currentStage?.fracModel?.startLinearFlowDT);
    };
    if (currentStage?.fracModel?.endLinearFlowDT > 0) {
      setLinearFlowEnd(currentStage?.fracModel?.endLinearFlowDT);
    };
  }, [currentStage?.fracModel?.startLinearFlowDT, currentStage?.fracModel?.endLinearFlowDT]);


  return (
    <Paper elevation={0} sx={{boxShadow: 1}} style={{height: 'auto', padding: 0, margin: '10px 20px', display: 'flex', flexDirection: 'column', backgroundColor: (theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')])}}>
      <div style={{width: 'calc(100% - 20px)', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', margin: '20px 20px 20px 20px'}}>
        <Typography variant="h6">
          Frac Model
        </Typography>
        
        <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
          
          <Button
            size="small"
            color={(darkMode ? "inherit" : "primary")}
            style={{
              color: (darkMode ? '#fff' : theme.palette.primary.main), 
              borderColor: (darkMode ? '#fff' : theme.palette.primary.main), 
              marginRight: 20, 
              height: 28,
              fontSize: 12
            }}
            variant="outlined"  
            startIcon={<FileDownload />}
            onClick={handleDownloadClick}
          >
            Download Pad Falloff CSV
          </Button>

          <FormControlLabel
            control={<Switch />} 
            label={<Typography style={{fontSize: 12}}>Lock Stage</Typography>}
            size="small" 
            checked={currentStage?.fracModelLocked ? true : false} 
            onChange={() => {
              if (socket && socket.readyState === 1) {
                socket?.send(JSON.stringify({
                  messageType: 'saveStage',
                  stage: {...currentStage, fracModelLocked: currentStage?.fracModelLocked ? false : true}
                }));
              };
              dispatch(toggleStageFracModelLock({ stageName: currentStage?.name }));
            }}
          />

          
        </div>
      </div>
      

      <div style={{display: 'flex', flexDirection: 'row', height: 580, width: '100%'}}>
        <RTFOFracModelHalfLengthProbabilityChart />
        <RTFOFracModelFracAreaProbabilityChart />
      </div>

      <Divider style={{margin: '0 10px'}} />

      {!currentStage?.fracModel ? 
      <div style={{width: '100%', height: 520, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        <Typography variant="body2">Stage Frac Model Results Not Available</Typography>
      </div>  :
    
      <>
        {currentStage?.fracModel?.model !== 'simple' && <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', position: 'relative', padding: '0px'}}>

          <div style={{width: '50%', height: 580, padding: '40px 0px 30px 0px', margin: 5, position: 'relative', outline: editMode && '5px solid red', zIndex: 420}}>
            <div 
              style={{
                zIndex: 69, 
                position: 'absolute', 
                left: '50%', 
                top: 0, 
                transform: 'translateX(-50%)', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
              }}
            >
              {editMode && <Typography variant="h6" color="red">EDIT MODE</Typography>}
            </div>
            <div 
              style={{
                zIndex: 69, 
                position: 'absolute', 
                left: '50%', 
                bottom: 0, 
                transform: 'translateX(-50%)', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
              }}
            >
              {editMode === 'start' ? <Typography variant="body2" color="red">Click to move the leakoff-dominated start time</Typography> : 
              editMode === 'end' ? <Typography variant="body2" color="red">Click to move the leakoff-dominated end time</Typography> : ''}
            </div>
            
            <div style={{display: 'flex', position: 'absolute', top: 10, left: 10}}>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{height: 24, zIndex: 420}}
                disabled={currentStage?.fracModelLocked}
              >
                <ToggleButton value="start" size="small" title="Edit Linear Time Start" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="end" size="small" title="Edit Linear Time End" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            {editMode && 
            <ToggleButtonGroup
              value={''}
              size="small"
              style={{height: 24, position: 'absolute', top: 10, right: 10, zIndex: 420}}
              onChange={() => {}}
            >
              <ToggleButton value={'1'} size="small" title="Cancel Changes" color={(darkMode ? "" : "primary")} onClick={handleCancelChanges}>
                <Cancel style={{fontSize: '16px'}} />
              </ToggleButton>
              <ToggleButton value={'2'} size="small" title="Save Changes" color={(darkMode ? "" : "primary")} onClick={handleSaveChanges}>
                <Save style={{fontSize: '16px'}} />
              </ToggleButton>
            </ToggleButtonGroup>}

            <RTFOFracModelDerivativeChart 
              editMode={editMode} 
              linearFlowStart={linearFlowStart} 
              setLinearFlowStart={setLinearFlowStart}
              linearFlowEnd={linearFlowEnd} 
              setLinearFlowEnd={setLinearFlowEnd}
            />
          </div>

          <div style={{width: '50%', height: 580, padding: '40px 0px 30px 0px', margin: 5, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
            {currentRTFO ? <RTFOFracModelChart well={currentWellName} />
            :
            <div>
              <Typography variant="body2">No data selected</Typography>
            </div>
            }
          </div>

          <Divider style={{margin: '0 10px'}} />
        </div>}
      </>}
      

      <div style={{width: '100%', height: 800}}>
        <RTFOFracModelDataTable currentRTFO={currentRTFO} darkMode={darkMode} userEmail={user?.value?.email} />
      </div>

    </Paper>
  );

  // FUNCTIONS
  function handleCancelChanges() {
    try {
      
      setEditMode(false);
      setLinearFlowStart(currentStage?.fracModel?.startLinearFlowDT);
      setLinearFlowEnd(currentStage?.fracModel?.endLinearFlowDT);

    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleSaveChanges() {
    try {

      setEditMode(false);
      dispatch(setIsLoadingData(true));

      socket?.send(JSON.stringify({
        messageType: 'recalculateFracModel',
        stage: {...currentStage, fracModel: {...currentStage?.fracModel, startLinearFlowDT: linearFlowStart, endLinearFlowDT: linearFlowEnd}},
        perfSchedule: currentRTFO?.perfSchedule,
        surveys: currentRTFO?.surveys,
        settings: currentRTFO?.settings
      }));

      enqueueSnackbar(`Stage ${currentStage?.name} saved successfully`, { variant: 'success' });


    } catch (error) {
      console.log(error);
      return;
    };
  };

  function handleDownloadClick() {
    try {

      if (!currentRTFO?.stageData || Object.keys(currentRTFO?.stageData)?.length === 0) return;

      console.log('Downloading pad frac model data CSV...');

      let headers = [
        'Well Name', 
        'Well UWI', 
        'Stage',
        'Date', 
        'Time (hr)', 
        'Pressure (MPa)', 
        'Pressure BH (MPa)'
      ];

      let csv = "";
      csv += headers.join(",") + "\r\n";


      for (const stageName of Object.keys(currentRTFO?.stageData)) {

        const falloffData = currentRTFO?.stageData?.[stageName]?.fracModel?.falloffData;

        if (!falloffData) continue;

        for (const d of falloffData) {
          let row = `${stageName?.split('_')?.[1]},${currentRTFO?.stageData?.[stageName]?.wellUwi},${stageName?.split('_')?.[2]},${d.date},${d.time},${d.pressure || 0},${(d.pressureBH / 1000) || 0}`;
          row += "\r\n";
          csv += row;
        };
      };

      const hiddenElement = document.createElement('a');  
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv);  
      hiddenElement.target = '_blank';  
      hiddenElement.download = `${currentRTFO?.name}_falloff_data_${new Date()?.toISOString()}.csv`;  
      hiddenElement.click();  

      return;

    } catch (error) {
      console.log(error);
    };
  };
};